/* eslint-disable react-hooks/refs -- The render-phase MRU ref mutation
   is intentional and load-bearing: see the long comment on `mountedIds`
   below. This is the only mutation; mountedIds itself is React state. */
import { useEffect, useRef, useState } from "react";
import { MULTIPLEX_DEBUG } from "@/lib/debug-flags";
import {
  appendWithLruEviction,
  filterMountedByScope,
  MULTIPLEX_CAP,
} from "./multiplex-eviction";

export { MULTIPLEX_CAP } from "./multiplex-eviction";

interface UseMultiplexMountedIdsArgs {
  selectedChannel: string | null;
  selectedWorkspace: string | null;
  channelWorkspaceMap: ReadonlyMap<string, string | null>;
  /**
   * Raw query result — `undefined` means the user-channels query hasn't
   * resolved yet. We skip the scope-evict effect in that case so a
   * not-yet-known channel isn't evicted spuriously.
   */
  allUserChannelsRaw: ReadonlyArray<unknown> | undefined;
  isMultiplexActive: boolean;
}

export function useMultiplexMountedIds({
  selectedChannel,
  selectedWorkspace,
  channelWorkspaceMap,
  allUserChannelsRaw,
  isMultiplexActive,
}: UseMultiplexMountedIdsArgs) {
  // Multiplexed MessageAreas (Linear-style kept-alive views).
  // Mount the active channel + the last N visited (within this session) so
  // return visits are truly zero-work — no remount, no Zero resubscribe,
  // no scroll-anchor reset. Linear's channel-switch trick. Capped at
  // MULTIPLEX_CAP to bound memory + Zero subscription count.
  //
  // Uses useState + setState-during-render rather than useMemo over the
  // LRU so the mounted set is STABLE across renders. mountedIds order is
  // never reordered on revisit — reordering a keyed list forces React to
  // unmount+remount moved children in this tree (losing scroll state).
  // MRU is tracked separately in mruOrderRef for eviction decisions: when
  // cap is hit, we evict the least-recently-used id from mountedIds,
  // which is the only mountedIds mutation that unmounts anything. Revisits
  // update the ref only — no render-phase setState, no fiber churn.
  // Set to 1 to effectively disable multiplex (only the active channel
  // stays mounted) while keeping the surrounding scaffolding intact.
  // Raise to 3–5 to re-enable Linear-style instant channel switches.
  const [mountedIds, setMountedIds] = useState<string[]>(() =>
    selectedChannel ? [selectedChannel] : [],
  );
  // MRU ordering lives outside React state: head = most-recently-used.
  const mruOrderRef = useRef<string[]>(
    selectedChannel ? [selectedChannel] : [],
  );
  if (selectedChannel) {
    // Refresh MRU on every render — cheap array reshuffle on a ref.
    const mru = mruOrderRef.current;
    if (mru[0] !== selectedChannel) {
      mruOrderRef.current = [
        selectedChannel,
        ...mru.filter((id) => id !== selectedChannel),
      ];
    }
  }
  if (selectedChannel && !mountedIds.includes(selectedChannel)) {
    // New channel: append (NOT prepend) so existing slot positions are
    // preserved. If cap is exceeded, evict the LRU id — which is the
    // mountedIds member ranked last in mruOrderRef.
    const next = appendWithLruEviction(
      mountedIds,
      selectedChannel,
      mruOrderRef.current,
      MULTIPLEX_CAP,
    );
    setMountedIds(next);
  }

  useEffect(() => {
    if (!MULTIPLEX_DEBUG) return;
    console.debug(
      `[mx] LAYER ${isMultiplexActive ? "SHOW" : "HIDE"}`,
      `active=${selectedChannel?.slice(-6) ?? "-"}`,
      `mounted=[${mountedIds.map((id) => id.slice(-6)).join(",")}]`,
    );
  }, [isMultiplexActive, selectedChannel, mountedIds]);

  // D.6: scope multiplex to current workspace
  // When the user switches workspaces, evict mountedIds entries
  // whose workspace_id doesn't match the active scope. DMs
  // (workspace_id IS NULL in the schema) persist everywhere since
  // they're workspace-universal. The active channel is never
  // evicted — its render is imminent, and evicting it would briefly
  // leave the multiplex with zero active instances.
  //
  // On home scope (selectedWorkspace === null), only DMs survive,
  // matching the product intent "Home is DM-first."
  //
  // When a channel is not yet in channelWorkspaceMap (user just
  // joined it; channels.byUser() hasn't caught up), map.get()
  // returns undefined → treated as a DM (kept). Self-corrects once
  // the query resolves.
  useEffect(() => {
    if (allUserChannelsRaw === undefined) return;
    setMountedIds((prev) => {
      const filtered = filterMountedByScope(
        prev,
        selectedChannel,
        channelWorkspaceMap,
        selectedWorkspace,
      );
      if (filtered.length === prev.length) return prev;
      if (MULTIPLEX_DEBUG) {
        const evicted = prev.filter((id) => !filtered.includes(id));
        console.debug(
          `[mx] SCOPE EVICT ws=${selectedWorkspace?.slice(-6) ?? "home"}`,
          `dropped=[${evicted.map((id) => id.slice(-6)).join(",")}]`,
          `kept=[${filtered.map((id) => id.slice(-6)).join(",")}]`,
        );
      }
      return filtered;
    });
  }, [
    selectedWorkspace,
    channelWorkspaceMap,
    selectedChannel,
    allUserChannelsRaw,
  ]);

  return { mountedIds };
}
